import { ChevronLeft, ChevronRight, ExternalLink, Sparkles, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

// ============================================================================
// Types
// ============================================================================

interface Announcement {
  id: string;
  version: string;
  titleKey: string;
  contentKey: string;
  imageUrl?: string;
  /** Optional "learn more" target, e.g. the release notes page for this week. */
  linkUrl?: string | null;
  createdAt: string;
}

// ============================================================================
// Component
// ============================================================================

export function WhatsNewModal() {
  const { t } = useTranslation('help');
  const { user, isAuthenticated } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch unseen announcements when user logs in
  useEffect(() => {
    if (!(isAuthenticated && user)) {
      setAnnouncements([]);
      return;
    }

    async function fetchAnnouncements() {
      setIsLoading(true);
      try {
        const response = await api.get<Announcement[]>('/announcements/unseen');
        if (response.data && response.data.length > 0) {
          setAnnouncements(response.data);
          setCurrentIndex(0);
          setOpen(true);
        }
      } catch {
        // Silently fail - announcements are not critical
      } finally {
        setIsLoading(false);
      }
    }

    fetchAnnouncements();
  }, [isAuthenticated, user]);

  const currentAnnouncement = announcements[currentIndex];

  const handleNext = useCallback(() => {
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, announcements.length]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const handleClose = useCallback(async () => {
    // Mark all announcements as seen
    try {
      await api.post('/announcements/seen-all');
    } catch {
      // Silently fail
    }
    setOpen(false);
    setAnnouncements([]);
  }, []);

  const handleDismissCurrent = useCallback(async () => {
    if (!currentAnnouncement) return;

    try {
      await api.post(`/announcements/${currentAnnouncement.id}/seen`);
    } catch {
      // Silently fail
    }

    // Remove current announcement from list
    const remaining = announcements.filter((_, i) => i !== currentIndex);
    if (remaining.length === 0) {
      setOpen(false);
      setAnnouncements([]);
    } else {
      setAnnouncements(remaining);
      setCurrentIndex(Math.min(currentIndex, remaining.length - 1));
    }
  }, [currentAnnouncement, announcements, currentIndex]);

  if (!currentAnnouncement || isLoading) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            {t('whatsNew.title')}
            <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              v{currentAnnouncement.version}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Announcement content */}
          {currentAnnouncement.imageUrl && (
            <div className="overflow-hidden rounded-lg">
              <img src={currentAnnouncement.imageUrl} alt="" className="h-48 w-full object-cover" />
            </div>
          )}

          <div className="space-y-2">
            <h3 className="text-lg font-semibold">{t(currentAnnouncement.titleKey)}</h3>
            <p className="whitespace-pre-line text-sm text-muted-foreground">
              {t(currentAnnouncement.contentKey)}
            </p>
          </div>

          {/* Navigation and actions */}
          <div className="flex items-center justify-between pt-4">
            <div className="flex items-center gap-2">
              {announcements.length > 1 && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    className="h-8 w-8"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {currentIndex + 1} / {announcements.length}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleNext}
                    disabled={currentIndex === announcements.length - 1}
                    className="h-8 w-8"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              {announcements.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismissCurrent}
                  className="text-muted-foreground"
                >
                  <X className="mr-1 h-4 w-4" />
                  {t('whatsNew.dismissOne')}
                </Button>
              )}
              {currentAnnouncement.linkUrl && (
                <Button variant="outline" asChild>
                  <a href={currentAnnouncement.linkUrl} target="_blank" rel="noopener noreferrer">
                    {t('whatsNew.learnMore')}
                    <ExternalLink className="ml-1 h-4 w-4" />
                  </a>
                </Button>
              )}
              <Button onClick={handleClose}>{t('whatsNew.close')}</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
